import * as z from 'zod';

/** Geocoder that produced a candidate site's address fields. */
export const GEOCODER_SOURCES = ['google'] as const;
export type GeocoderSource = (typeof GEOCODER_SOURCES)[number];

/** How a candidate site entered the workspace. */
export const INPUT_SHAPES = [
  'address_search',
  'map_click',
  'coordinate_search',
] as const;
export type InputShape = (typeof INPUT_SHAPES)[number];

/**
 * The geocoded fields may be blank when the geocoder could not resolve them — they are never
 * guessed. `placeId` is the Google Maps Platform ToS-durable anchor, which makes the geocoded
 * fields cache: re-derivable from it, so they can be refreshed or dropped without losing the
 * site's identity.
 */
export interface CandidateSite {
  id: string;
  latitude: number;
  longitude: number;
  country: string;
  postalCode: string;
  /** Land / Kanton / Bundesland display name. */
  adminRegion: string;
  /** e.g. DE-BY, CH-ZH, AT-9; blank if underivable. */
  iso3166_2: string;
  displayAddress: string;
  placeId: string;
  geocoderSource: GeocoderSource;
  inputShape: InputShape;
  /** Defaults to the resolved address. */
  label: string;
  notes: string;
  /** Retail / Kundenparkplatz, a fraction. Complementary with `onRouteShare`: the two sum to 1. */
  destinationShare: number;
  /** Zwischenstop auf der Fahrtstrecke, a fraction. */
  onRouteShare: number;
  /** ISO-8601. */
  createdAt: string;
  /** The tenancy scope — the server's `company_id`, surfaced as a string. */
  organizationId: string;
}

export const candidateSiteSchema = z.object({
  id: z.string(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  country: z.string(),
  postalCode: z.string(),
  adminRegion: z.string(),
  iso3166_2: z.string(),
  displayAddress: z.string(),
  placeId: z.string(),
  geocoderSource: z.enum(['google']),
  inputShape: z.enum(INPUT_SHAPES),
  label: z.string(),
  notes: z.string(),
  // Per-field range only; the sum invariant is owned by the edit UI and the server.
  destinationShare: z.number().min(0).max(1),
  onRouteShare: z.number().min(0).max(1),
  createdAt: z.string(),
  organizationId: z.string(),
}) satisfies z.ZodType<CandidateSite>;
